import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { Field, Form, constantField, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  enrollment_status,
  enrollmentid,
  invoiceid,
  payment_method,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { renderAsync } from "../../client/renderasync";
import { renderSaveButton } from "../../client/savebutton";
import { parseFormulaType, parsePaymentMethod } from "../../parsing_from_ur";
import * as routes from "../../routes";
import { planning_slot_contents_lesson } from "./client";
import currency from "currency.js";
import { wrapInParentheses } from "../../render";

type enr = {
  enrollmentid: enrollmentid;
  status: enrollment_status;
  invoiceid: invoiceid | null;
  payments: {
    method: payment_method;
    amount: currency;
  }[];
};

export function renderDeleteLessongroupPanel(
  deletingLessongroupS: Source<boolean>,
  l: planning_slot_contents_lesson,
  enrollmentsP: Promise<enr[]>,
  opts: { handleDeleteDone: () => void },
) {
  const cancelButton = (
    <button onclick={() => deletingLessongroupS.set(false)}>
      {"Annuleer"}
    </button>
  );

  const contents: string | HTMLElement | HTMLElement[] = (() => {
    const t = parseFormulaType(l.lessongroup.type);
    return renderAsync(enrollmentsP, (enrs) => {
      if (t.tag === "Group" || t.tag === "Camp") {
        if (enrs.length > 0) {
          if (
            enrs.every(
              (enr) => enr.status === "Stopped" || enr.status === "TrialLesson",
            )
          ) {
            return (
              <div>
                <article>
                  {"Deze lessenreeks bevat nog stopgezette inschrijvingen en zal worden stopgezet en alle toekomstige lessen verwijderd"}
                </article>
                <div style="text-align: right">
                  {cancelButton}
                  {renderSaveButton({
                    save: () =>
                      rpc(routes.stopGroupLessongroupSpec, undefined, {
                        lessongroupid: l.lessongroup.id,
                        action: "stop" as const,
                        fromdate: l.lesson.date,
                      }),
                    text: {
                      normal: "Stop lessenreeks",
                      success: "Gelukt!",
                    },
                    reload: opts.handleDeleteDone,
                    timeout: 1000,
                  })}
                </div>
              </div>
            );
          } else {
            return (
              <div>
                <article>
                  {"Deze lessenreeks heeft nog actieve inschrijvingen en kan je niet stoppen of verwijderen. Stop of verwijder eerst alle inschrijvingen."}
                </article>
                <div style="text-align: right">{cancelButton}</div>
              </div>
            );
          }
        } else {
          return (
            <div>
              <article>
                {"Deze lessenreeks bevat geen inschrijvingen en zal volledig verwijderd worden."}
              </article>
              <div style="text-align: right">
                {cancelButton}
                {renderSaveButton({
                  save: () =>
                    rpc(routes.stopGroupLessongroupSpec, undefined, {
                      lessongroupid: l.lessongroup.id,
                      action: "delete" as const,
                      fromdate: l.lesson.date,
                    }),
                  text: {
                    normal: "Verwijder",
                    success: "Gelukt!",
                  },
                  reload: opts.handleDeleteDone,
                  timeout: 1000,
                })}
              </div>
            </div>
          );
        }
      } else if (t.tag === "Private") {
        if (enrs.length !== 1) {
          return <div>Technical error: more than one enrollment found</div>;
        }
        const enr = enrs[0];
        const form = makeStopForm(enr);
        return (
          <div>
            {form.render()}
            <div style="text-align: right">
              {cancelButton}
              {renderSaveButton({
                formS: form.s,
                save: (f) =>
                  rpc(routes.stopPrivateLessongroupSpec, undefined, {
                    lessongroupid: l.lessongroup.id,
                    fromdate: l.lesson.date,
                    ...f,
                  }),
                text: {
                  normal: "Sla op",
                  success: "Gelukt!",
                },
                reload: opts.handleDeleteDone,
                timeout: 1000,
              })}
            </div>
          </div>
        );
      } else {
        return checkAllCasesHandled(t);
      }
    });
  })();

  return <div className={style.panel + style.slide_in}>{contents}</div>;
}

export function makeStopForm(enr: enr) {
  const form = new Form({})
    .addField("action", [], (): Promise<Field<"stop" | "delete">> => {
      if (enr.payments.some((p) => parsePaymentMethod(p.method) === "Online")) {
        return constantFieldWithRender(
          "stop",
          <article>
            {"Deze inschrijving werd online betaald en zal dus worden stopgezet, maar niet volledig verwijderd."}
          </article>,
        );
      } else {
        const options =
          enr.status === "ActiveEnrollment"
            ? ["stop" as const, "delete" as const]
            : ["delete" as const];
        return selectBox({
          values: options,
          initial: options[0],
          show: (s) =>
            s === "stop"
              ? "Stopzetten" +
                " " +
                wrapInParentheses("lessen in het verleden blijven")
              : "Volledig verwijderen",
          label: "Wat wil je met deze inschrijving doen?",
        });
      }
    })
    .addField(
      "invoice_action",
      ["action"],
      ({ action }): Promise<Field<"delete" | "credit" | "nothing">> => {
        if (action === "delete" || enr.invoiceid === null) {
          return constantField("delete");
        } else {
          return selectBox({
            values: ["nothing", "credit", "delete"],
            initial: "nothing",
            show: (s) =>
              s === "credit"
                ? "Crediteer onbetaald bedrag"
                : s === "delete"
                ? "Verwijder"
                : "Doe niets",
            label: "Wat wil je met de factuur doen?",
          });
        }
      },
    )
    .addField(
      "periodfee_action",
      ["invoice_action"],
      async ({
        invoice_action,
      }): Promise<Field<"delete" | "credit" | "nothing">> => {
        const status = await rpc(
          routes.getStoppingEnrollmentPeriodfeeStatusSpec,
          undefined,
          {
            enrollmentid: enr.enrollmentid,
          },
        );
        if (status.tag === "no_pf") {
          return constantField("nothing");
        } else if (status.tag === "more_enrollments") {
          return constantFieldWithRender(
            "nothing",
            <article>
              <label for="">{"Jaargeld"}</label>
              <div className={style.input_like}>
                {"Deze student heeft nog andere inschrijvingen dus het jaargeld blijft bestaan."}
              </div>
            </article>,
          );
        } else if (status.tag === "is_last_enrollment") {
          return selectBox({
            values:
              invoice_action === "delete"
                ? ["delete", "credit", "nothing"]
                : ["credit", "delete", "nothing"],
            initial: invoice_action === "delete" ? "delete" : "credit",
            show: (s) =>
              s === "credit"
                ? "Crediteer onbetaald bedrag"
                : s === "delete"
                ? "Verwijder"
                : "Doe niets",
            label: "Dit is de enige inschrijving voor deze student dit jaar. Wat wil je met het jaargeld doen?",
          });
        } else {
          return checkAllCasesHandled(status);
        }
      },
    )
    .build();

  return form;
}
